import {
  MonitoringCheck,
  MonitoringCheckSpec,
  MonitoringConfig,
  MonitoringSection,
  MonitoringStatus,
  SWEEP_STALE_MS,
} from '@/features/monitoring/monitoringApi';

// Type-only: this module is pure logic and must not drag the React/HeroUI
// component tree in behind it.
import type { PillTone } from '@/pages/Settings/settingsUi';

// ---------------------------------------------------------------------------
// Pure logic behind the monitoring page. Kept out of Monitoring.tsx so the
// rules that actually cost debugging time — the four-state verdict, the
// page-level roll-up, the stale-sweep test, and above all the whole-section
// payload build — can be exercised without a React tree or the store.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Health side
// ---------------------------------------------------------------------------

export type CheckState = 'pending' | 'failing' | 'degraded' | 'healthy';

// Four display states, not two. The ORDER is the contract: a check that has
// never run is `pending`, not "failing", and a check that is serving fine off
// last-known-good data is `degraded`, not "healthy". Reading the state off `ok`
// alone would say "everything is healthy" for ~35 minutes next to an EPG page
// showing the same feed broken.
export function checkState(row: MonitoringCheck): CheckState {
  if (row.pending)	return 'pending';	// waiting for the first result
  if (!row.ok)		return 'failing';
  if (row.degraded)	return 'degraded';	// still serving, on last good data

  return 'healthy';
}

export const STATE_TONE: { [state in CheckState]: PillTone } = {
  pending:	'info',
  failing:	'bad',
  degraded:	'warn',
  healthy:	'ok',
};

export function checkTone(row: MonitoringCheck): PillTone {
  return STATE_TONE[checkState(row)];
}

export type CheckFamily = 'kpn' | 'epg' | 'skyway' | 'mailer' | 'http' | 'other';

// Card order on the page. `other` last so a kind added by a newer server still
// renders instead of vanishing.
export const FAMILY_ORDER: CheckFamily[] = ['kpn', 'epg', 'skyway', 'mailer', 'http', 'other'];

export const FAMILY_ICON: { [family in CheckFamily]: string } = {
  kpn:		'cloud_sync',
  epg:		'connected_tv',
  skyway:	'settings_remote',
  mailer:	'mail',
  http:		'lan',
  other:	'help_outline',
};

const KIND_FAMILY: { [kind: string]: CheckFamily } = {
  kpn_auth:			'kpn',
  provider_epg:		'kpn',
  epg_sources:		'epg',
  skyway_inventory:	'skyway',
  skyway_probe:		'skyway',
  mailer:			'mailer',
  http:				'http',
};

export const CHECK_KINDS = Object.keys(KIND_FAMILY);

export function knownKind(kind: string): boolean {
  return CHECK_KINDS.indexOf(kind) >= 0;
}

export function checkFamily(kind: string): CheckFamily {
  return KIND_FAMILY[kind] || 'other';
}

// The instance a per-instance check belongs to. `kpn:KPN-1:auth` -> `KPN-1`;
// for `http` EVERYTHING after the first colon is the operator's own name, which
// is why this is not a plain `parts[1]`.
export function checkInstance(check: string): string {
  const parts = (check || '').split(':');

  if (parts[0] === 'http')
    return parts.slice(1).join(':');

  return parts.length >= 3 ? parts[1] : '';
}

export interface FamilyGroup {
  family:		CheckFamily;
  rows:			MonitoringCheck[];
}

// Server order inside a group is preserved — the registry is already sorted by
// `check`, and re-sorting here would make a row jump between polls.
export function groupChecks(rows: MonitoringCheck[]): FamilyGroup[] {
  return FAMILY_ORDER
    .map((family) => ({
      family:	family,
      rows:		rows.filter((r) => checkFamily(r.kind) === family),
    }))
    .filter((g) => g.rows.length > 0);
}

// The worst state in a group, for the card's own pill.
export function worstTone(rows: MonitoringCheck[]): PillTone {
  const states = rows.map(checkState);

  if (states.indexOf('failing') >= 0)
    return 'bad';

  if (states.indexOf('degraded') >= 0)
    return 'warn';

  if (states.length && states.every((st) => st === 'pending'))
    return 'info';

  return 'ok';
}

// Seconds since an RFC3339 stamp. -1 when the value is absent or unparseable,
// so a caller can tell "never" apart from "just now".
export function secondsSince(iso: string, nowMs: number): number {
  if (!iso)
    return -1;

  const t = Date.parse(iso);

  if (isNaN(t))
    return -1;

  return Math.max(0, Math.floor((nowMs - t) / 1000));
}

// The scheduler has stopped. Only meaningful while checks exist — an empty
// registry with no sweep is the normal "monitoring does not run here" state.
export function sweepStale(lastSweepAt: string, nowMs: number): boolean {
  const t = Date.parse(lastSweepAt || '');

  if (isNaN(t))
    return false;

  return nowMs - t > SWEEP_STALE_MS;
}

export type VerdictKind =
  | 'loading'		// no snapshot yet
  | 'idle'			// nothing derived here — a normal multi-server state
  | 'failing'		// at least one check is down
  | 'degraded'		// everything answers, but something upstream is broken
  | 'stale'			// the sweep has stopped, so none of this is current
  | 'pending'		// every check is still waiting for its first result
  | 'healthy';

export interface Verdict {
  kind:			VerdictKind;
  failing:		number;
  degraded:		number;
  pending:		number;
  total:		number;
}

// "Everything is healthy" is only true when no row is failing AND no row is
// degraded — degraded exists precisely so this page cannot contradict the EPG
// page for the half hour before a failing feed crosses its alert threshold.
//
// `stale` outranks pending/healthy but not failing/degraded: a stopped
// scheduler makes the snapshot untrustworthy, yet a failure it did catch is
// still the more useful headline.
export function pageVerdict(status: MonitoringStatus | null, nowMs: number): Verdict {
  const rows = status ? status.checks : [];

  const states = rows.map(checkState);
  const count = (want: CheckState) => states.filter((st) => st === want).length;

  const failing = count('failing');
  const degraded = count('degraded');
  const pending = count('pending');

  const verdict = (kind: VerdictKind): Verdict => ({
    kind:		kind,
    failing:	failing,
    degraded:	degraded,
    pending:	pending,
    total:		rows.length,
  });

  if (!status)					return verdict('loading');
  if (!rows.length)				return verdict('idle');
  if (failing)					return verdict('failing');
  if (degraded)					return verdict('degraded');
  if (sweepStale(status.last_sweep_at, nowMs))
  								return verdict('stale');
  if (pending === rows.length)	return verdict('pending');

  return verdict('healthy');
}

export const VERDICT_TONE: { [kind in VerdictKind]: PillTone } = {
  loading:	'info',
  idle:		'info',
  failing:	'bad',
  degraded:	'warn',
  stale:	'warn',
  pending:	'info',
  healthy:	'ok',
};

export const VERDICT_ICON: { [kind in VerdictKind]: string } = {
  loading:	'hourglass_empty',
  idle:		'pause_circle_outline',
  failing:	'error_outline',
  degraded:	'warning_amber',
  stale:	'update_disabled',
  pending:	'hourglass_empty',
  healthy:	'check_circle_outline',
};

// ---------------------------------------------------------------------------
// Server log — the two config.json keys beside the section
// ---------------------------------------------------------------------------

export const LOG_LEVEL_NORMAL	= 'info';
export const LOG_LEVEL_VERBOSE	= 'debug';
export const LOG_FORMAT_TEXT	= 'text';
export const LOG_FORMAT_JSON	= 'json';

// Only two of the four levels are offered: in practice there are two questions,
// "is it running normally" and "I am investigating something". `warn` and
// `error` stay valid for a hand-edited config.json.
export const LOG_LEVELS_SHOWN	= [LOG_LEVEL_NORMAL, LOG_LEVEL_VERBOSE];
export const LOG_FORMATS_SHOWN	= [LOG_FORMAT_TEXT, LOG_FORMAT_JSON];

// "" means the key is absent, i.e. the built-in default — show the default's
// option rather than an empty dropdown.
export function resolveStored(stored: string, fallback: string): string {
  return stored === '' ? fallback : stored;
}

// A stored value outside the offered set (a hand-edited `warn`) is added as a
// third option instead of being silently reset to Normal on the next save.
// Returns '' when nothing extra is needed.
export function extraStoredOption(stored: string, shown: string[]): string {
  if (stored === '' || shown.indexOf(stored) >= 0)
    return '';

  return stored;
}

// ---------------------------------------------------------------------------
// Settings form
// ---------------------------------------------------------------------------

// Synthetic stable ids, for the same reason the EPG source rows carry one:
// array-index keys pin DOM nodes to screen positions, so removing row 2 moves
// focus and the caret onto the value that slid up into its place.
let nextEditId = 1;

export interface EmailEdit {
  id:				number;
  value:			string;
}

export interface CustomCheckEdit {
  id:				number;
  // The spec AS LOADED. Spread back at save time so a per-check key this UI
  // does not render survives the whole-section replace.
  spec:				MonitoringCheckSpec;
  type:				string;
  name:				string;
  url:				string;
  method:			string;
  expectStatus:		string;
  bodyContains:		string;
  intervalSeconds:	string;
}

export interface MonitoringForm {
  emails:			EmailEdit[];
  realertHours:		string;
  disabled:			boolean;
  intervalSeconds:	string;
  timeoutSeconds:	string;
  checks:			CustomCheckEdit[];
  logLevel:			string;
  logFormat:		string;

  serverName:		string;

  // The two secrets hold the MASK as loaded until the admin pastes over it.
  teamsWebhook:		string;
  teamsEnabled:		boolean;
  mentions:			EmailEdit[];

  ticketsUrl:		string;
  ticketsToken:		string;
  ticketsProject:	string;
  ticketsEnabled:	boolean;
}

// 0 is "unset, use the server default" on every numeric field here, so it
// renders as a BLANK field against a default-valued placeholder rather than as
// a literal zero an operator would read as "never".
export function numToField(value?: number): string {
  return value ? String(value) : '';
}

export function fieldToNum(value: string): number {
  const v = (value || '').trim();

  return v === '' ? 0 : parseInt(v, 10);
}

export function toEmailEdits(emails?: string[]): EmailEdit[] {
  return (emails || []).map((value) => ({ id: nextEditId++, value: value }));
}

export function newEmailEdit(): EmailEdit {
  return { id: nextEditId++, value: '' };
}

export function toCheckEdit(spec: MonitoringCheckSpec): CustomCheckEdit {
  return {
    id:					nextEditId++,
    spec:				spec,
    type:				spec.type || 'http',
    name:				spec.name || '',
    url:				spec.url || '',
    method:				spec.method || 'GET',
    expectStatus:		numToField(spec.expect_status),
    bodyContains:		spec.body_contains || '',
    intervalSeconds:	numToField(spec.interval_seconds),
  };
}

export function newCheckEdit(): CustomCheckEdit {
  return {
    id:					nextEditId++,
    spec:				{ type: 'http', name: '', url: '' },
    type:				'http',
    name:				'',
    url:				'',
    method:				'GET',
    expectStatus:		'',
    bodyContains:		'',
    intervalSeconds:	'',
  };
}

function str(val: any): string {
  return typeof val === 'string' ? val : '';
}

function strList(val: any): string[] {
  return Array.isArray(val) ? val.filter((v: any) => typeof v === 'string') : [];
}

// ---------------------------------------------------------------------------
// The two secrets
// ---------------------------------------------------------------------------

export const SECRET_MASK = 'REDACTED';

// Exact, never a substring test: `sig=REDACTEDX` is somebody's real value.
export function isTokenMask(value: string): boolean {
  return (value || '').trim() === SECRET_MASK;
}

export const WEBHOOK_MASK_RE = /[?&][^=&]*=REDACTED(?=&|$)/;

export function isWebhookMask(value: string): boolean {
  const v = (value || '').trim();

  return v === SECRET_MASK || WEBHOOK_MASK_RE.test(v);
}

// The server reads the section's keys ignoring case, so a hand edit can leave
// `TEAMS_WEBHOOK` in config.json (§3.10). Where the value of `key` lives:
//   - `key` itself when it is present;
//   - the one variant spelling when there is exactly one and no `key` — the
//     page shows it and saves it back as `key`, which the server repairs the
//     same way;
//   - nowhere ('') when there is none, or several variants. Several are left
//     alone: the server refuses that save with its own explanation.
export function secretKeyIn(section: MonitoringSection, key: string): string {
  if (Object.prototype.hasOwnProperty.call(section, key))
    return key;

  const variants = Object.keys(section).filter((k) => k !== key && k.toLowerCase() === key);

  return variants.length === 1 ? variants[0] : '';
}

export function loadedSecret(section: MonitoringSection, key: string): string {
  const at = secretKeyIn(section, key);

  return at ? str(section[at]) : '';
}

// The field still holds the mask exactly as GET returned it.
export function webhookUntouched(value: string, loaded: MonitoringSection): boolean {
  const stored = loadedSecret(loaded, 'teams_webhook');

  return isWebhookMask(stored) && (value || '').trim() === stored.trim();
}

export function tokenUntouched(value: string, loaded: MonitoringSection): boolean {
  const stored = loadedSecret(loaded, 'tickets_token');

  return isTokenMask(stored) && isTokenMask(value);
}

export function toForm(cfg: MonitoringConfig): MonitoringForm {
  const sec = cfg.monitoring;

  return {
    emails:				toEmailEdits(sec.alert_emails),
    realertHours:		numToField(sec.realert_hours),
    disabled:			!!sec.disabled,
    intervalSeconds:	numToField(sec.check_defaults?.interval_seconds),
    timeoutSeconds:		numToField(sec.check_defaults?.timeout_seconds),
    checks:				(sec.checks || []).map(toCheckEdit),
    logLevel:			resolveStored(cfg.log_level, LOG_LEVEL_NORMAL),
    logFormat:			resolveStored(cfg.log_format, LOG_FORMAT_TEXT),

    serverName:			str(sec.server_name),

    teamsWebhook:		loadedSecret(sec, 'teams_webhook'),
    teamsEnabled:		!sec.teams_disabled,
    mentions:			toEmailEdits(strList(sec.teams_mentions)),

    ticketsUrl:			str(sec.tickets_url),
    ticketsToken:		loadedSecret(sec, 'tickets_token'),
    ticketsProject:		str(sec.tickets_project),
    ticketsEnabled:		!sec.tickets_disabled,
  };
}

// The saved settings carry this channel — what the test buttons actually test.
export function savedHasTeams(loaded: MonitoringSection): boolean {
  return loadedSecret(loaded, 'teams_webhook').trim() !== '';
}

export function savedHasTickets(loaded: MonitoringSection): boolean {
  return	str(loaded.tickets_url).trim() !== '' &&
  			loadedSecret(loaded, 'tickets_token').trim() !== '';
}

function setOrDelete(target: any, key: string, value: any) {
  // A blank numeric field means "the key is not in the file". Writing 0 instead
  // would be a guess about how the server reads it, and `expect_status: 0` in
  // particular could mean "no status ever matches".
  if (value)
    target[key] = value;
  else
    delete target[key];
}

export function buildCheckSpec(row: CustomCheckEdit): MonitoringCheckSpec {
  const spec: MonitoringCheckSpec = {
    ...row.spec,
    type:		row.type,
    name:		row.name.trim(),
    url:		row.url.trim(),
    method:		row.method,
  };

  setOrDelete(spec, 'expect_status', fieldToNum(row.expectStatus));
  setOrDelete(spec, 'interval_seconds', fieldToNum(row.intervalSeconds));
  setOrDelete(spec, 'body_contains', row.bodyContains.trim());

  return spec;
}

// THE function to get right. POST replaces the whole section, so this starts
// from the section as LOADED and mutates it — never from the fields this form
// happens to render. `overrides[]`, which has no editor, and any key a newer
// server added both survive on the spread.
//
// `channels` is false while the page has not seen a server new enough to mask
// the secrets (§9) — the Teams / tickets / server-name editors are hidden then,
// and their keys ride along on the spread exactly as loaded.
export function buildSection(
  loaded: MonitoringSection,
  form: MonitoringForm,
  channels: boolean = true,
): MonitoringSection {
  const out: MonitoringSection = {
    ...loaded,
    alert_emails:	form.emails.map((e) => e.value.trim()).filter((v) => v !== ''),
    disabled:		form.disabled,
    checks:			form.checks.map(buildCheckSpec),
  };

  const defaults: any = { ...(loaded.check_defaults || {}) };
  setOrDelete(defaults, 'interval_seconds', fieldToNum(form.intervalSeconds));
  setOrDelete(defaults, 'timeout_seconds', fieldToNum(form.timeoutSeconds));
  out.check_defaults = defaults;

  setOrDelete(out, 'realert_hours', fieldToNum(form.realertHours));

  if (!channels)
    return out;

  setOrDelete(out, 'server_name', form.serverName.trim());

  // An untouched mask goes back EXACTLY as loaded — that is how the server
  // knows to keep the stored secret. Never trimmed, repaired or rebuilt.
  putSecret(out, loaded, 'teams_webhook',
            webhookUntouched(form.teamsWebhook, loaded)
              ? loadedSecret(loaded, 'teams_webhook')
              : form.teamsWebhook.trim());

  setOrDelete(out, 'teams_disabled', !form.teamsEnabled);
  setOrDelete(out, 'teams_mentions', mentionList(form.mentions));

  setOrDelete(out, 'tickets_url', form.ticketsUrl.trim());
  putSecret(out, loaded, 'tickets_token',
            tokenUntouched(form.ticketsToken, loaded)
              ? loadedSecret(loaded, 'tickets_token')
              : form.ticketsToken.trim());
  setOrDelete(out, 'tickets_project', form.ticketsProject.trim());
  setOrDelete(out, 'tickets_disabled', !form.ticketsEnabled);

  return out;
}

// Writes a secret under its exact key. A single variant spelling it was loaded
// from is dropped, so the value is saved once, as `key`. Several variants are
// left alone (see secretKeyIn).
function putSecret(out: MonitoringSection, loaded: MonitoringSection, key: string, value: string) {
  const at = secretKeyIn(loaded, key);

  if (at && at !== key)
    delete out[at];

  setOrDelete(out, key, value);
}

function mentionList(rows: EmailEdit[]): string[] | undefined {
  const list = rows.map((e) => e.value.trim()).filter((v) => v !== '');

  return list.length ? list : undefined;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

// Deliberately permissive, and the same shape the Mailer page already uses. The
// server is authoritative and SKIPS an unusable address rather than rejecting
// the save, so a mismatch here must never block one.
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const URL_RE = /^https?:\/\/.+/i;

export function emailInvalid(value: string): boolean {
  const v = (value || '').trim();

  // CR/LF would be a header-injection attempt; the server refuses those too.
  return v !== '' && (!EMAIL_RE.test(v) || /[\r\n]/.test(v));
}

// The one recipient mistake that DOES block the save (§3.6): a web address.
// Almost always the Teams webhook pasted into the wrong card — saved there it
// would sit in config.json in clear and be shown to every admin.
export function emailIsUrl(value: string): boolean {
  return (value || '').indexOf('://') >= 0;
}

// Addresses the server will store but never use. Reported as a warning, never
// as a save blocker — see EMAIL_RE. A web address is left out: it already
// blocks the save on its own row, and repeating it here would echo what may
// be a webhook secret into a second place on the screen.
export function skippedEmails(emails: EmailEdit[]): string[] {
  return emails
    .map((e) => e.value.trim())
    .filter((v) => v !== '' && !emailIsUrl(v) && emailInvalid(v));
}

// ---------------------------------------------------------------------------
// Server name (§5.1)
// ---------------------------------------------------------------------------

export const SERVER_NAME_MAX = 64;

export type ServerNameError = 'none' | 'chars' | 'long';

// Letters, marks, digits, punctuation, symbols and the plain space — so one
// line and no control characters. The server would SAVE a bad name and then
// ignore it ("skipped: server_name: …"), which is why the page blocks it.
const SERVER_NAME_BAD_RE = /[^\p{L}\p{M}\p{N}\p{P}\p{S} ]/u;

export function serverNameError(value: string): ServerNameError {
  const v = (value || '').trim();

  if (SERVER_NAME_BAD_RE.test(v))
    return 'chars';

  // Characters, not UTF-16 units: an emoji is one.
  if (Array.from(v).length > SERVER_NAME_MAX)
    return 'long';

  return 'none';
}

// The same rule the server applies to every subject and title: the chosen name
// with the machine name in brackets, or the machine name alone when there is
// no name or it IS the machine name.
export function serverLabel(name: string, hostname: string): string {
  const n = (name || '').trim();

  if (n === '' || n.toLowerCase() === (hostname || '').toLowerCase())
    return hostname;

  return n + ' (' + hostname + ')';
}

// ---------------------------------------------------------------------------
// Microsoft Teams (§5.2, §5.3)
// ---------------------------------------------------------------------------

export type WebhookError = 'none' | 'masked' | 'url';

// No whitespace and no control characters anywhere in a secret URL.
// eslint-disable-next-line no-control-regex
const CONTROL_OR_SPACE_RE = /[\s\x00-\x1f\x7f]/;

// The authority part carries a `user@`.
const URL_CREDENTIALS_RE = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*@/i;

export function webhookError(value: string, loaded: MonitoringSection): WebhookError {
  const v = (value || '').trim();

  if (v === '')
    return 'none';				// Teams is off

  if (webhookUntouched(v, loaded))
    return 'none';				// the stored URL is kept

  if (isWebhookMask(v))
    return 'masked';			// a mask, but not the loaded one

  if (	!/^https:\/\//i.test(v) ||
		CONTROL_OR_SPACE_RE.test(v) ||
		v.indexOf('#') >= 0 ||
		URL_CREDENTIALS_RE.test(v)
	 )
    return 'url';

  let parsed: URL;
  try {
    parsed = new URL(v);
  } catch {
    return 'url';
  }

  if (parsed.username || parsed.password || !(parsed.searchParams.get('sig') || '').trim())
    return 'url';

  return 'none';
}

export const MENTIONS_MAX = 25;
export const MENTION_NAME_MAX = 100;
export const MENTION_ADDRESS_MAX = 254;

export const MENTION_RE = /^(?:[^<>,]*<[^\s@<>]+@[^\s@<>]+>|[^\s@<>]+@[^\s@<>]+)$/;

export type MentionError = 'none' | 'format' | 'duplicate' | 'many';

// The address part of `Name <user@domain>` or `user@domain`. '' when the row
// is not one of the two shapes.
export function mentionAddress(value: string): string {
  const v = (value || '').trim();

  if (!MENTION_RE.test(v))
    return '';

  const lt = v.indexOf('<');

  return lt >= 0 ? v.slice(lt + 1, -1) : v;
}

function mentionShapeOk(value: string): boolean {
  const v = (value || '').trim();
  const address = mentionAddress(v);

  // eslint-disable-next-line no-control-regex
  if (!address || /[\x00-\x1f\x7f]/.test(v))
    return false;

  const lt = v.indexOf('<');
  const name = lt >= 0 ? v.slice(0, lt).trim() : '';

  return	Array.from(name).length <= MENTION_NAME_MAX &&
  			address.length <= MENTION_ADDRESS_MAX;
}

// One entry per ROW (blank rows included, as 'none'). The server would save a
// bad list and then IGNORE ALL OF IT ("skipped: teams_mentions: …"), so
// nobody would be mentioned — hence blocked here rather than warned about.
// The 26th filled row onwards reports 'many'; the second row with an address
// already listed (ignoring case) reports 'duplicate'.
export function mentionErrors(rows: EmailEdit[]): MentionError[] {
  const seen: { [address: string]: boolean } = {};
  let filled = 0;

  return rows.map((row) => {
    const v = row.value.trim();

    if (v === '')
      return 'none';

    filled++;

    if (!mentionShapeOk(v))
      return 'format';

    const key = mentionAddress(v).toLowerCase();
    if (seen[key])
      return 'duplicate';
    seen[key] = true;

    if (filled > MENTIONS_MAX)
      return 'many';

    return 'none';
  });
}

// ---------------------------------------------------------------------------
// Tickets (§5.4)
// ---------------------------------------------------------------------------

export type TicketsFieldError = 'none' | 'pair' | 'format' | 'repaste';

export interface TicketsErrors {
  url:			TicketsFieldError;
  token:		TicketsFieldError;
  project:		boolean;
}

export const TICKETS_TOKEN_RE = /^[\x21-\x7e]{16,256}$/;
export const TICKETS_PROJECT_RE = /^[A-Za-z0-9][A-Za-z0-9._/-]*$/;
export const TICKETS_PROJECT_MAX = 200;

// http:// only towards this machine itself.
const LOOPBACK_HTTP_RE = /^http:\/\/(?:localhost|127\.0\.0\.1|\[::1\])(?:[:/]|$)/i;

export function ticketsUrlOk(value: string): boolean {
  const v = (value || '').trim();

  if (!/^https:\/\//i.test(v) && !LOOPBACK_HTTP_RE.test(v))
    return false;

  return	!CONTROL_OR_SPACE_RE.test(v) &&
  			v.indexOf('?') < 0 &&
  			v.indexOf('#') < 0 &&
  			!URL_CREDENTIALS_RE.test(v);
}

export function ticketsErrors(form: MonitoringForm, loaded: MonitoringSection): TicketsErrors {
  const url = form.ticketsUrl.trim();
  const token = form.ticketsToken.trim();
  const project = form.ticketsProject.trim();

  const none: TicketsErrors = { url: 'none', token: 'none', project: false };

  // Both blank: tickets are off, and the project means nothing.
  if (url === '' && token === '')
    return none;

  if (url === '' || token === '')
    return { url: 'pair', token: 'pair', project: false };

  const errs: TicketsErrors = {
    url:		ticketsUrlOk(url) ? 'none' : 'format',
    token:		'none',
    project:	project !== '' &&
    			(project.length > TICKETS_PROJECT_MAX || !TICKETS_PROJECT_RE.test(project)),
  };

  // The stored token is never sent to a new address — the server refuses that,
  // so the token has to be pasted again whenever the URL moves.
  if (tokenUntouched(token, loaded)) {
    if (url !== str(loaded.tickets_url).trim())
      return { ...errs, url: errs.url === 'format' ? 'format' : 'repaste', token: 'repaste' };
  } else if (!TICKETS_TOKEN_RE.test(token)) {
    errs.token = 'format';
  }

  return errs;
}

function ticketsInvalid(errs: TicketsErrors): boolean {
  return errs.url !== 'none' || errs.token !== 'none' || errs.project;
}

// ---------------------------------------------------------------------------
// Who is told
// ---------------------------------------------------------------------------

export function teamsActive(form: MonitoringForm): boolean {
  return form.teamsEnabled && form.teamsWebhook.trim() !== '';
}

export function ticketsActive(form: MonitoringForm): boolean {
  return	form.ticketsEnabled &&
  			form.ticketsUrl.trim() !== '' &&
  			form.ticketsToken.trim() !== '';
}

// §3.7: silence only when NO channel would carry an alert. A paused channel
// counts as absent.
export function nobodyTold(form: MonitoringForm): boolean {
  return	!form.emails.some((e) => e.value.trim() !== '') &&
  			!teamsActive(form) &&
  			!ticketsActive(form);
}

// Blank passes: every numeric field here means "server default" when empty.
export function intFieldInvalid(value: string, min?: number, max?: number): boolean {
  const v = (value || '').trim();

  if (v === '')
    return false;

  if (!/^-?[0-9]+$/.test(v))
    return true;

  const n = parseInt(v, 10);

  if (min !== undefined && n < min)	return true;
  if (max !== undefined && n > max)	return true;

  return false;
}

export type NameError = 'none' | 'required' | 'duplicate';
export type UrlError = 'none' | 'required' | 'format';

export interface CheckRowErrors {
  name:				NameError;
  url:				UrlError;
  expectStatus:		boolean;
  intervalSeconds:	boolean;
}

// `name` becomes the identity `http:<name>`, so two rows sharing one would
// collapse into a single registry entry — and a single React key on the health
// side. Blocked rather than left to the server.
export function checkRowErrors(
  row: CustomCheckEdit,
  index: number,
  all: CustomCheckEdit[],
): CheckRowErrors {
  const name = row.name.trim();
  const url = row.url.trim();

  let nameError: NameError = 'none';
  if (name === '')
    nameError = 'required';
  else if (all.some((other, j) => j !== index && other.name.trim() === name))
    nameError = 'duplicate';

  let urlError: UrlError = 'none';
  if (url === '')
    urlError = 'required';
  else if (!URL_RE.test(url))
    urlError = 'format';

  return {
    name:				nameError,
    url:				urlError,
    expectStatus:		intFieldInvalid(row.expectStatus, 100, 599),
    intervalSeconds:	intFieldInvalid(row.intervalSeconds, 0),
  };
}

export function checkRowInvalid(errs: CheckRowErrors): boolean {
  return	errs.name !== 'none' ||
  			errs.url !== 'none' ||
  			errs.expectStatus ||
  			errs.intervalSeconds;
}

// `body_contains` can never match a HEAD response — there is no body. The row
// reports it server-side, but by then the operator has already saved a check
// that silently cannot pass.
export function bodyContainsUnreachable(row: CustomCheckEdit): boolean {
  return row.method === 'HEAD' && row.bodyContains.trim() !== '';
}

// `loaded` is the section as GET returned it (for the two masks); `channels`
// is false while the Teams / tickets / server-name editors are hidden (§9) — a
// field the admin cannot see must never block the save.
export function formInvalid(
  form: MonitoringForm,
  loaded: MonitoringSection = {},
  channels: boolean = true,
): boolean {
  if (intFieldInvalid(form.realertHours))
    return true;

  if (	intFieldInvalid(form.intervalSeconds, 0) ||
		intFieldInvalid(form.timeoutSeconds, 0)
     )
    return true;

  if (form.emails.some((e) => emailIsUrl(e.value)))
    return true;

  if (form.checks.some((row, i) => checkRowInvalid(checkRowErrors(row, i, form.checks))))
    return true;

  if (!channels)
    return false;

  return	serverNameError(form.serverName) !== 'none' ||
  			webhookError(form.teamsWebhook, loaded) !== 'none' ||
  			mentionErrors(form.mentions).some((e) => e !== 'none') ||
  			ticketsInvalid(ticketsErrors(form, loaded));
}

export function formWarnings(form: MonitoringForm): boolean {
  return !!skippedEmails(form.emails).length || form.checks.some(bodyContainsUnreachable);
}

// ---------------------------------------------------------------------------
// Change detection
// ---------------------------------------------------------------------------

function checkEditsEqual(a: CustomCheckEdit, b: CustomCheckEdit): boolean {
  return	a.type === b.type &&
  			a.name === b.name &&
  			a.url === b.url &&
  			a.method === b.method &&
  			a.expectStatus === b.expectStatus &&
  			a.bodyContains === b.bodyContains &&
  			a.intervalSeconds === b.intervalSeconds;
}

// Blank rows are dropped at save, so they are no change either.
function listChanged(rows: EmailEdit[], savedRows: EmailEdit[]): boolean {
  const list = rows.map((e) => e.value.trim()).filter((v) => v !== '');
  const savedList = savedRows.map((e) => e.value.trim()).filter((v) => v !== '');

  return	list.length !== savedList.length ||
  			list.some((v, i) => v !== savedList[i]);
}

// Field-by-field, not a JSON compare of the built payload: the built section
// carries the loaded object's key order, so a stringify would report a change
// the first time a key this form normalises was absent from the file.
export function formChanged(form: MonitoringForm, saved: MonitoringForm): boolean {
  if (	form.realertHours !== saved.realertHours ||
		form.disabled !== saved.disabled ||
		form.intervalSeconds !== saved.intervalSeconds ||
		form.timeoutSeconds !== saved.timeoutSeconds ||
		form.logLevel !== saved.logLevel ||
		form.logFormat !== saved.logFormat
	 )
    return true;

  if (listChanged(form.emails, saved.emails))
    return true;

  // Trimmed throughout, so an untouched mask counts as UNCHANGED: Save stays
  // disabled, and the page never posts a mask for nothing.
  if (	form.serverName.trim() !== saved.serverName.trim() ||
		form.teamsWebhook.trim() !== saved.teamsWebhook.trim() ||
		form.teamsEnabled !== saved.teamsEnabled ||
		form.ticketsUrl.trim() !== saved.ticketsUrl.trim() ||
		form.ticketsToken.trim() !== saved.ticketsToken.trim() ||
		form.ticketsProject.trim() !== saved.ticketsProject.trim() ||
		form.ticketsEnabled !== saved.ticketsEnabled ||
		listChanged(form.mentions, saved.mentions)
	 )
    return true;

  return	form.checks.length !== saved.checks.length ||
  			form.checks.some((row, i) => !checkEditsEqual(row, saved.checks[i]));
}

// The value to POST for a logging key, or undefined to leave the stored one
// alone. Omitting an untouched level is what keeps an ABSENT key absent —
// posting its resolved default would pin the file to a value the operator never
// chose and stop it tracking the built-in default. `fallback` has to be the
// BUILT-IN default, not the current selection, or an absent key would compare
// equal to whatever the operator just picked.
export function logKeyPayload(
  current: string,
  stored: string,
  fallback: string,
): string | undefined {
  return current === resolveStored(stored, fallback) ? undefined : current;
}

// A saved `log_format` only takes effect at the next restart — the logger is
// built once at boot. The save succeeds and says so, but if the page does not
// repeat it an admin watches for JSON lines that cannot arrive.
export function formatNeedsRestart(form: MonitoringForm, cfg: MonitoringConfig | null): boolean {
  if (!cfg)
    return false;

  return form.logFormat !== resolveStored(cfg.log_format, LOG_FORMAT_TEXT);
}

// The inline result of a test button. The server's text is shown exactly as it
// arrives — it never contains the webhook or the token.
export interface TestOutcome {
  ok:			boolean;
  text:			string;
}

export function testOutcome(envelope: any): TestOutcome {
  const first = Array.isArray(envelope?.errors) ? envelope.errors[0] : null;

  if (!first || typeof first !== 'object')
    return { ok: false, text: '' };

  return {
    ok:		first.code === 0,
    text:	typeof first.error === 'string' ? first.error : '',
  };
}

// §8.3: `code: 0` can still carry a "skipped:" clause — part of the save will
// not do anything. A plain green toast there is a lie.
export function messageIsQualified(message: string): boolean {
  return /skipped|restart/i.test(message || '');
}
